/**
 * SearchableSelect — single-value select with type-to-filter input
 * (MUI Autocomplete dressed as the standard outlined Select used across
 * the registry screens). Drop-in replacement for FormControl+Select:
 * include the "All …" choice as a { value: '', label } option and the
 * behavior matches the old empty MenuItem; the clear (×) button resets
 * to '' as well.
 */

import React, { useMemo } from 'react';
import { Autocomplete, TextField, Popper, PopperProps, SxProps, Theme } from '@mui/material';

/**
 * Popup sized to its content instead of the input: at least as wide as the
 * field, grows to the longest option, capped to the viewport so long labels
 * (section paths, config types) stay readable.
 */
function FitContentPopper(props: PopperProps) {
  const anchor = props.anchorEl as HTMLElement | null;
  const minWidth = anchor && 'clientWidth' in anchor ? anchor.clientWidth : undefined;
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: 'fit-content', minWidth, maxWidth: 'min(90vw, 640px)' }}
    />
  );
}

export type SearchableSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type SearchableSelectProps = {
  label: string;
  /** '' = nothing selected (or the explicit '' option when provided) */
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
};

export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  size = 'small',
  fullWidth = false,
  disabled = false,
  placeholder,
  sx,
}: SearchableSelectProps) {
  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  return (
    <Autocomplete
      size={size}
      options={options}
      value={selected}
      onChange={(_e, opt) => onChange(opt?.value ?? '')}
      getOptionLabel={(o) => o.label}
      getOptionDisabled={(o) => Boolean(o.disabled)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      autoHighlight
      disabled={disabled}
      fullWidth={fullWidth}
      sx={sx}
      PopperComponent={FitContentPopper}
      slotProps={{
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }
          }
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
    />
  );
}
